/**
 * Department Assessor (Annual Assessment) Form
 * Task form: Department, Assessor, Date of Impact Assessment (read-only),
 * Impact found?, Rationale, Impact priority, Scope of impact, Additional Comments.
 */

import React, { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

export interface DepartmentAssessmentFormValues {
  impactFound: string;
  rationale: string;
  impactPriority: string;
  /** Selected market/region codes (scope of impact) */
  scopeOfImpact: string[];
  additionalComments: string;
}

export interface DepartmentAssessmentFormProps {
  /** Read-only: department name */
  department?: string;
  /** Read-only: assessor (e.g. email) */
  assessor?: string;
  /** Read-only: date of impact assessment */
  assessmentDate?: string;
  /** Initial form values */
  initialValues?: Partial<DepartmentAssessmentFormValues>;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Impact priority options (optional select) */
  impactPriorityOptions?: Array<{ value: string; label: string }>;
  /** Options for scope of impact (markets/regions: code + name) */
  scopeOptions?: Array<{ code: string; name: string }>;
  /** Called when user clicks Cancel */
  onCancel?: () => void;
  /** Called when user submits */
  onSubmit?: (values: DepartmentAssessmentFormValues) => void;
  /** When true, disables the submit button (e.g. task already completed) */
  submitDisabled?: boolean;
}

const defaultPriorityOptions = [
  { value: "", label: "Select priority level" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

export const DepartmentAssessmentForm: React.FC<DepartmentAssessmentFormProps> = ({
  department = "Legal & Compliance",
  assessor = "john.miller@company.com",
  assessmentDate = "01-Mar-2026",
  initialValues,
  loading = false,
  error: errorProp = null,
  impactPriorityOptions = defaultPriorityOptions,
  scopeOptions = [],
  onCancel,
  onSubmit,
  submitDisabled = false,
}) => {
  const normalizeScope = (v: string | string[] | undefined): string[] => {
    if (Array.isArray(v)) return v;
    if (typeof v === "string" && v.trim()) return v.split(",").map((s) => s.trim()).filter(Boolean);
    return [];
  };
  const [impactFound, setImpactFound] = useState(initialValues?.impactFound ?? "");
  const [rationale, setRationale] = useState(initialValues?.rationale ?? "");
  const [impactPriority, setImpactPriority] = useState(initialValues?.impactPriority ?? "");
  const [scopeOfImpact, setScopeOfImpact] = useState<string[]>(() =>
    normalizeScope(initialValues?.scopeOfImpact)
  );
  const [additionalComments, setAdditionalComments] = useState(
    initialValues?.additionalComments ?? ""
  );
  const [touched, setTouched] = useState({ rationale: false, impactFound: false });

  const handleSubmit = useCallback(() => {
    setTouched({ rationale: true, impactFound: true });
    if (!impactFound.trim()) return;
    if (!rationale.trim()) return;
    onSubmit?.({
      impactFound: impactFound.trim(),
      rationale: rationale.trim(),
      impactPriority,
      scopeOfImpact,
      additionalComments,
    });
  }, [impactFound, rationale, impactPriority, scopeOfImpact, additionalComments, onSubmit]);

  const showRationaleError = touched.rationale && !rationale.trim();
  const showImpactFoundError = touched.impactFound && !impactFound;

  if (loading) {
    return (
      <Box id="department-assessment-form" sx={{ maxWidth: 720, py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box id="department-assessment-form" sx={{ maxWidth: 720 }}>
      {errorProp && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorProp}
        </Alert>
      )}
      <Card
        variant="outlined"
        sx={{
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "grey.200",
          overflow: "hidden",
        }}
      >
        <CardHeader
          title="Task"
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "grey.200",
            "& .MuiCardHeader-title": { fontSize: "1rem", fontWeight: 600 },
          }}
        />
        <CardContent sx={{ px: 3, pt: 2, pb: 3, "&:last-child": { pb: 3 } }}>
          {/* Row 1: Department, Assessor */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Box
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "grey.700",
                  mb: 1,
                }}
                htmlFor="department"
              >
                Department
              </Box>
              <TextField
                id="department"
                fullWidth
                size="small"
                value={department}
                disabled
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "grey.600",
                    bgcolor: "grey.50",
                  },
                }}
              />
            </Box>
            <Box>
              <Box
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "grey.700",
                  mb: 1,
                }}
                htmlFor="assessor"
              >
                Assessor
              </Box>
              <TextField
                id="assessor"
                fullWidth
                size="small"
                value={assessor}
                disabled
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "grey.600",
                    bgcolor: "grey.50",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Row 2: Date of Impact Assessment, Impact found? */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Box
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "grey.700",
                  mb: 1,
                }}
                htmlFor="assessmentDate"
              >
                Date of Impact Assessment
              </Box>
              <TextField
                id="assessmentDate"
                fullWidth
                size="small"
                value={assessmentDate}
                disabled
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "grey.600",
                    bgcolor: "grey.50",
                  },
                }}
              />
            </Box>
            <FormControl size="small" fullWidth required error={!!showImpactFoundError}>
              <InputLabel id="impact-found-label">Impact found?</InputLabel>
              <Select
                labelId="impact-found-label"
                id="impactFound"
                value={impactFound}
                label="Impact found?"
                onChange={(e) => setImpactFound(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, impactFound: true }))}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Select...</em>
                </MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
              {showImpactFoundError && (
                <Box component="span" sx={{ fontSize: "0.75rem", color: "error.main", mt: 0.5 }}>
                  Required
                </Box>
              )}
            </FormControl>
          </Box>

          {/* Rationale (required) */}
          <Box sx={{ mb: 3 }}>
            <Box
              component="label"
              sx={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "grey.900",
                mb: 1,
              }}
              htmlFor="rationale"
            >
              Rationale <Box component="span" sx={{ color: "error.main" }}>*</Box>
            </Box>
            <TextField
              id="rationale"
              fullWidth
              multiline
              minRows={4}
              placeholder="Provide detailed rationale for your impact assessment decision..."
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              onBlur={() => setTouched((p) => ({ ...p, rationale: true }))}
              error={!!showRationaleError}
              helperText={showRationaleError ? "Required" : undefined}
              sx={{ "& textarea": { minHeight: 128 } }}
            />
          </Box>

          {/* Row 3: Impact priority (optional), Scope of impact (optional) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel id="priority-label">Impact priority</InputLabel>
              <Select
                labelId="priority-label"
                id="priority"
                value={impactPriority}
                label="Impact priority"
                onChange={(e) => setImpactPriority(e.target.value)}
                displayEmpty
              >
                {impactPriorityOptions.map((opt) => (
                  <MenuItem key={opt.value || "empty"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <Box component="span" sx={{ fontSize: "0.75rem", color: "grey.500", mt: 0.5 }}>
                (Optional)
              </Box>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="scope-label">Scope of impact</InputLabel>
              <Select
                labelId="scope-label"
                id="scope-of-impact"
                multiple
                value={scopeOfImpact}
                label="Scope of impact"
                onChange={(e) => {
                  const v = e.target.value;
                  setScopeOfImpact(typeof v === "string" ? v.split(",") : v);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return "Select affected markets/regions";
                  const names = selected
                    .map((code) => scopeOptions.find((o) => o.code === code)?.name ?? code)
                    .join(", ");
                  return names.length > 40 ? `${names.slice(0, 40)}…` : names;
                }}
                displayEmpty
                sx={{ minHeight: 40 }}
              >
                {scopeOptions.map((opt) => (
                  <MenuItem key={opt.code} value={opt.code}>
                    <Checkbox checked={scopeOfImpact.indexOf(opt.code) > -1} size="small" />
                    <ListItemText primary={opt.name} />
                  </MenuItem>
                ))}
                {scopeOptions.length === 0 && (
                  <MenuItem disabled>No markets/regions available</MenuItem>
                )}
              </Select>
              <Box component="span" sx={{ fontSize: "0.75rem", color: "grey.500", mt: 0.5 }}>
                (Optional)
              </Box>
            </FormControl>
          </Box>

          {/* Additional Comments (optional) */}
          <Box sx={{ mb: 3 }}>
            <Box
              component="label"
              sx={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "grey.900",
                mb: 1,
              }}
              htmlFor="comments"
            >
              Additional Comments{" "}
              <Box component="span" sx={{ color: "grey.500", fontSize: "0.75rem", fontWeight: 400 }}>
                (Optional)
              </Box>
            </Box>
            <TextField
              id="comments"
              fullWidth
              multiline
              minRows={3}
              placeholder="Any additional information, concerns, or recommendations..."
              value={additionalComments}
              onChange={(e) => setAdditionalComments(e.target.value)}
              sx={{ "& textarea": { minHeight: 96 } }}
            />
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitDisabled}
              sx={{
                bgcolor: "#4f46e5",
                "&:hover": { bgcolor: "#4338ca" },
              }}
            >
              Submit Assessment
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
